import { Scene, Vector3, MeshBuilder, Mesh, PointLight, Color3, StandardMaterial } from "@babylonjs/core";
import { BaseZone } from "./BaseZone.js";
import { getSharedMaterials } from "../materials.js";
import { DynamicTextureSurface } from "../surfaces/DynamicTextureSurface.js";
import { mergeStaticChildren } from "../mergeStatic.js";
import { addContactShadow, buildCafeTable, buildStool } from "../props.js";
import { PropLibrary } from "../propLibrary.js";
import type { SurfaceTheme } from "../surfaces/types.js";

/**
 * The Mall Mart food court: a hall south of the atrium with vendor stalls
 * along the walls and seating in the middle. No delivery contracts exist yet,
 * so every stall is a themed "Coming Soon" placeholder. Each stall config
 * reserves a vendorIntegration slot so a future delivery API (Uber Eats,
 * Postmates, ...) can claim a stall without rebuilding the space.
 */

export interface FoodStallConfig {
  name: string;
  accent: string;
  /** Reserved for a future ordering integration; always null this pass. */
  vendorIntegration: null;
}

const STALLS: FoodStallConfig[] = [
  { name: "Slice Slice Baby", accent: "#d9534f", vendorIntegration: null },
  { name: "Wok This Way", accent: "#e8a33d", vendorIntegration: null },
  { name: "Holy Guacamole", accent: "#57a05a", vendorIntegration: null },
  { name: "The Grind Coffee", accent: "#8a5a3a", vendorIntegration: null },
  { name: "Cluck Norris", accent: "#d97b29", vendorIntegration: null },
  { name: "Seoul Food", accent: "#c14b6e", vendorIntegration: null },
  { name: "Frank's Franks", accent: "#b04a3a", vendorIntegration: null },
  { name: "Scoop Dreams", accent: "#6f9fd8", vendorIntegration: null },
];

// Hall extents (world space): x -30..30, z -75..-35, south of the atrium.
const HALF_W: number = 30;
const NEAR_Z: number = -35;
const FAR_Z: number = -75;
const HEIGHT: number = 8;

export class FoodCourtZone extends BaseZone {
  private floor: Mesh | undefined;

  constructor(scene: Scene) {
    super("food-court", scene, new Vector3(0, 0, (NEAR_Z + FAR_Z) / 2));
    this.entryPosition = new Vector3(0, 1.7, NEAR_Z - 3);
  }

  async build(): Promise<void> {
    const mats = getSharedMaterials(this.scene);
    const depth: number = NEAR_Z - FAR_Z;
    const centerZ: number = (NEAR_Z + FAR_Z) / 2;

    // Floor + ceiling (positions are world-space; root sits at origin offset 0)
    this.root.position = Vector3.Zero();
    const floor: Mesh = MeshBuilder.CreateGround(
      "fc-floor", { width: HALF_W * 2, height: depth }, this.scene
    );
    floor.position = new Vector3(0, 0, centerZ);
    floor.material = mats.foodCourtFloor;
    floor.parent = this.root;
    floor.checkCollisions = true;
    this.floor = floor;

    const ceil: Mesh = MeshBuilder.CreateBox(
      "fc-ceil", { width: HALF_W * 2, height: 0.3, depth }, this.scene
    );
    ceil.position = new Vector3(0, HEIGHT, centerZ);
    ceil.material = mats.ceiling;
    ceil.parent = this.root;

    // Walls: south (far), east, west, and north segments flanking the atrium opening (12 wide).
    const walls: Array<{ name: string; pos: Vector3; w: number; d: number }> = [
      { name: "fc-wall-s", pos: new Vector3(0, HEIGHT / 2, FAR_Z), w: HALF_W * 2, d: 0.4 },
      { name: "fc-wall-e", pos: new Vector3(HALF_W, HEIGHT / 2, centerZ), w: 0.4, d: depth },
      { name: "fc-wall-w", pos: new Vector3(-HALF_W, HEIGHT / 2, centerZ), w: 0.4, d: depth },
      { name: "fc-wall-n-l", pos: new Vector3(-(6 + (HALF_W - 6) / 2), HEIGHT / 2, NEAR_Z), w: HALF_W - 6, d: 0.4 },
      { name: "fc-wall-n-r", pos: new Vector3(6 + (HALF_W - 6) / 2, HEIGHT / 2, NEAR_Z), w: HALF_W - 6, d: 0.4 },
      { name: "fc-wall-n-lintel", pos: new Vector3(0, HEIGHT - 1.75, NEAR_Z), w: 12, d: 0.4 },
    ];
    for (const c of walls) {
      const wall: Mesh = MeshBuilder.CreateBox(
        c.name, { width: c.w, height: c.name.endsWith("lintel") ? 3.5 : HEIGHT, depth: c.d }, this.scene
      );
      wall.position = c.pos;
      wall.material = mats.corridorWall;
      wall.parent = this.root;
      wall.checkCollisions = true;
    }

    // Stalls: 3 along the west wall, 3 along the east wall, 2 on the south wall.
    const stallSpots: Array<{ pos: Vector3; rotY: number }> = [];
    for (let i: number = 0; i < 3; i++) {
      stallSpots.push({ pos: new Vector3(-HALF_W + 2.2, 0, -42 - i * 11), rotY: Math.PI / 2 });
      stallSpots.push({ pos: new Vector3(HALF_W - 2.2, 0, -42 - i * 11), rotY: -Math.PI / 2 });
    }
    stallSpots.push({ pos: new Vector3(-10, 0, FAR_Z + 2.2), rotY: 0 });
    stallSpots.push({ pos: new Vector3(10, 0, FAR_Z + 2.2), rotY: 0 });

    for (let i: number = 0; i < STALLS.length && i < stallSpots.length; i++) {
      this.buildStall(STALLS[i], stallSpots[i].pos, stallSpots[i].rotY, i);
    }

    // Seating: round tables with stools
    const tableSpots: Vector3[] = [];
    for (let row: number = 0; row < 3; row++) {
      for (let col: number = 0; col < 4; col++) {
        tableSpots.push(new Vector3(-12 + col * 8, 0, -45 - row * 10));
      }
    }
    for (let i: number = 0; i < tableSpots.length; i++) {
      this.buildTable(tableSpots[i], i);
    }

    // Lights
    for (const p of [new Vector3(-15, HEIGHT - 1.5, -45), new Vector3(15, HEIGHT - 1.5, -45), new Vector3(-15, HEIGHT - 1.5, -65), new Vector3(15, HEIGHT - 1.5, -65)]) {
      const light: PointLight = new PointLight(`fc-light-${p.x}-${p.z}`, p, this.scene);
      // Warm restaurant light: the hall gets no skylight sun, and neutral
      // 0.65 read as a dim mauve room on the live screenshots.
      light.diffuse = new Color3(1.0, 0.94, 0.84);
      light.intensity = 0.9;
      light.range = 26;
      light.parent = this.root;
    }

    mergeStaticChildren(this.root, (m) => m === this.floor);
  }

  private buildStall(stall: FoodStallConfig, pos: Vector3, rotY: number, index: number): void {
    const mats = getSharedMaterials(this.scene);
    const sin: number = Math.sin(rotY);
    const cos: number = Math.cos(rotY);
    // Local +z (facing the room) maps to world (sin, cos).

    // Back panel against the wall
    const back: Mesh = MeshBuilder.CreateBox(
      `fc-stall-back-${index}`, { width: 8, height: 4.5, depth: 0.3 }, this.scene
    );
    back.position = new Vector3(pos.x - sin * 1.8, 2.25, pos.z - cos * 1.8);
    back.rotation.y = rotY;
    back.material = mats.shopWall;
    back.parent = this.root;
    back.checkCollisions = true;

    // Counter with an overhanging top slab
    const counter: Mesh = MeshBuilder.CreateBox(
      `fc-stall-counter-${index}`, { width: 7, height: 1.05, depth: 1.1 }, this.scene
    );
    counter.position = new Vector3(pos.x + sin * 1.2, 0.525, pos.z + cos * 1.2);
    counter.rotation.y = rotY;
    counter.material = mats.woodFixture;
    counter.parent = this.root;
    counter.checkCollisions = true;

    const counterTop: Mesh = MeshBuilder.CreateBox(
      `fc-stall-countertop-${index}`, { width: 7.3, height: 0.07, depth: 1.4 }, this.scene
    );
    counterTop.position = new Vector3(pos.x + sin * 1.2, 1.09, pos.z + cos * 1.2);
    counterTop.rotation.y = rotY;
    counterTop.material = mats.metalFixture;
    counterTop.parent = this.root;

    // Sloped awning in the stall's accent color over the counter
    const awningMat: StandardMaterial = new StandardMaterial(`fc-awning-mat-${index}`, this.scene);
    awningMat.diffuseColor = Color3.FromHexString(stall.accent);
    awningMat.specularColor = new Color3(0, 0, 0);
    const awning: Mesh = MeshBuilder.CreateBox(
      `fc-stall-awning-${index}`, { width: 8.2, height: 0.06, depth: 2.0 }, this.scene
    );
    awning.position = new Vector3(pos.x + sin * 0.6, 4.0, pos.z + cos * 0.6);
    awning.rotation.y = rotY;
    awning.rotation.x = 0.35; // slopes down toward the seating
    awning.material = awningMat;
    awning.parent = this.root;

    // Menu board with the stall name + Coming Soon
    const theme: SurfaceTheme = {
      background: "#1a1d21",
      foreground: "#f4f6f8",
      accent: stall.accent,
      fontFamily: "sans-serif",
      fontFamilySerif: "serif",
    };
    const board: DynamicTextureSurface = new DynamicTextureSurface({
      scene: this.scene,
      name: `fc-stall-board-${index}`,
      widthM: 6.5,
      heightM: 2.2,
      resolution: { width: 832, height: 282 },
      theme,
      emissive: true,
    });
    board.placeAt({
      position: new Vector3(pos.x - sin * 1.6, 3.2, pos.z - cos * 1.6),
      rotationY: rotY, // faces the seating area, same way as the stall
      parent: this.root,
    });
    board.setContent({
      title: stall.name,
      subtitle: "COMING SOON",
      bodyLines: ["This spot is waiting on a vendor"],
    });
  }

  private buildTable(pos: Vector3, index: number): void {
    buildCafeTable(this.scene, this.root, `fc-table-${index}`, pos);
    const lib: PropLibrary = PropLibrary.for(this.scene);
    for (let s: number = 0; s < 3; s++) {
      const angle: number = (s / 3) * Math.PI * 2 + index * 0.7; // vary seating orientation
      const seatPos: Vector3 = new Vector3(
        pos.x + Math.cos(angle) * 1.25, pos.y, pos.z + Math.sin(angle) * 1.25
      );
      if (lib.isAvailable("plastic_monobloc_chair_01")) {
        // Yaw the chair to face the table centre (Babylon yaw measures from
        // +z toward +x, hence atan2(dx, dz)).
        const faceTable: number = Math.atan2(pos.x - seatPos.x, pos.z - seatPos.z);
        const name: string = `fc-chair-${index}-${s}`;
        lib.instantiate("plastic_monobloc_chair_01", this.root, seatPos, { rotationY: faceTable });
        const hull: Mesh = MeshBuilder.CreateBox(
          `${name}-hull`, { width: 0.55, height: 0.95, depth: 0.55 }, this.scene
        );
        hull.position = new Vector3(seatPos.x, 0.48, seatPos.z);
        hull.isVisible = false;
        hull.checkCollisions = true;
        hull.parent = this.root;
        addContactShadow(this.scene, this.root, name, seatPos, 0.42);
      } else {
        buildStool(this.scene, this.root, `fc-stool-${index}-${s}`, seatPos);
      }
    }
  }

  override floorMeshes(): Mesh[] {
    return this.floor === undefined ? [] : [this.floor];
  }
}
